import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, ArrowLeft, Loader2, Package, CreditCard, 
  Check, AlertTriangle, Truck, Lock, ChevronRight, MapPin
} from 'lucide-react';
import SquarePaymentForm from '../components/SquarePaymentForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

export default function ShopCheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cartId = searchParams.get('cart');
  
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  
  // Form state
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB'
  });
  
  const [shippingMethod, setShippingMethod] = useState('delivery'); // delivery or collection
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  
  useEffect(() => {
    if (!cartId) {
      navigate('/shop');
      return;
    }
    loadCart();
  }, [cartId, navigate]);

  const loadCart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/cart/${cartId}`);
      if (res.ok) {
        const data = await res.json();
        if (!data.items?.length) {
          toast.error('Cart is empty');
          navigate('/shop');
          return;
        }
        setCart(data);
      } else {
        toast.error('Cart not found');
        navigate('/shop');
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const requiresShipping = cart?.items?.some(item => item.product_type === 'physical');
  const isCollection = shippingMethod === 'collection';
  const shippingCost = (requiresShipping && !isCollection) ? (shippingAddress.country === 'GB' ? 3.99 : 9.99) : 0;
  const subtotal = cart?.subtotal || 0;
  const discount = discountApplied?.amount || 0;
  const total = subtotal + shippingCost - discount;

  // Silent form validation for UI state (no toasts)
  const isFormValid = () => {
    if (!customerInfo.name.trim()) return false;
    if (!customerInfo.email.trim() || !/\S+@\S+\.\S+/.test(customerInfo.email)) return false;
    if (requiresShipping && !isCollection) {
      if (!shippingAddress.line1.trim()) return false;
      if (!shippingAddress.city.trim()) return false;
      if (!shippingAddress.postcode.trim()) return false;
    }
    return true;
  };

  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setApplyingDiscount(true);
    try {
      const res = await fetch(`${API_URL}/api/shop/validate-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode.trim(), subtotal })
      });

      if (res.ok) {
        const data = await res.json();
        setDiscountApplied({
          code: data.code,
          amount: data.discount_amount,
          display: data.discount_display
        });
        toast.success(`Discount applied: ${data.discount_display}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Invalid discount code');
        setDiscountApplied(null);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error('Failed to apply discount code');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
    toast.info('Discount removed');
  };

  // Form validation with toast errors (for submission)
  const validateForm = () => {
    if (!customerInfo.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!customerInfo.email.trim() || !/\S+@\S+\.\S+/.test(customerInfo.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    // Only validate shipping address if delivery is selected
    if (requiresShipping && !isCollection) {
      if (!shippingAddress.line1.trim()) {
        toast.error('Please enter your address');
        return false;
      }
      if (!shippingAddress.city.trim()) {
        toast.error('Please enter your city');
        return false;
      }
      if (!shippingAddress.postcode.trim()) {
        toast.error('Please enter your postcode');
        return false;
      }
    }
    return true;
  };

  // Success handler for payment form
  const handlePaymentSuccess = (data) => {
    localStorage.removeItem('shop_cart_id');
    if (data.order) {
      setCompletedOrder(data.order);
    }
    setOrderComplete(true);
  };
  
  // Error handler for payment form
  const handlePaymentError = (errorMessage) => {
    console.error('Payment error:', errorMessage);
    setProcessing(false);
  };
  
  // Build custom payment body for the SquarePaymentForm
  const getCheckoutPayload = () => {
    const payload = {
      cart_id: cartId,
      customer_email: customerInfo.email.trim(),
      customer_name: customerInfo.name.trim(),
      shipping_method: shippingMethod,
      discount_code: discountCode || null
    };
    
    // Only include shipping address for delivery orders
    if (requiresShipping && !isCollection) {
      payload.shipping_address = {
        name: shippingAddress.name || customerInfo.name,
        line1: shippingAddress.line1,
        line2: shippingAddress.line2 || null,
        city: shippingAddress.city,
        county: shippingAddress.county || null,
        postcode: shippingAddress.postcode,
        country: shippingAddress.country,
        phone: customerInfo.phone || null
      };
    }
    // For collection, don't send shipping_address (it's optional in the model)
    // The backend will just store customer contact info from customer_name/customer_email
    
    return payload;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  // Order Complete View
  if (orderComplete && completedOrder) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Order Confirmed!</h1>
            <p className="text-slate-600 mb-6">
              Thank you for your order. We&apos;ve sent a confirmation email to{' '}
              <span className="font-medium">{completedOrder.customer_email}</span>
            </p>
            
            <Card className="text-left mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Order #{completedOrder.order_id.slice(0, 12)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {completedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">{formatCurrency(item.price * item.quantity)}</p>
                  </div>
                ))}
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{formatCurrency(completedOrder.subtotal)}</span>
                  </div>
                  {completedOrder.shipping_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Shipping</span>
                      <span>{formatCurrency(completedOrder.shipping_cost)}</span>
                    </div>
                  )}
                  {completedOrder.discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(completedOrder.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t">
                    <span>Total Paid</span>
                    <span>{formatCurrency(completedOrder.total_amount)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Button asChild>
              <Link to="/shop">
                Continue Shopping
                <ChevronRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/shop')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shop
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Checkout</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    data-testid="checkout-name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="07123 456789"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shipping Method */}
            {requiresShipping && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Delivery Method
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Delivery Option */}
                  <label 
                    className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                      shippingMethod === 'delivery' 
                        ? 'border-violet-500 bg-violet-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="delivery"
                      checked={shippingMethod === 'delivery'}
                      onChange={(e) => setShippingMethod(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-900">Delivery</p>
                          <p className="text-sm text-slate-500">Delivered to your door</p>
                        </div>
                        <p className="font-medium text-slate-900">
                          {shippingAddress.country === 'GB' ? '£3.99' : '£9.99'}
                        </p>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {shippingAddress.country === 'GB' ? '3-5 business days' : '7-14 business days'}
                      </p>
                    </div>
                  </label>
                  
                  {/* Collection Option */}
                  <label 
                    className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                      shippingMethod === 'collection' 
                        ? 'border-violet-500 bg-violet-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="collection"
                      checked={shippingMethod === 'collection'}
                      onChange={(e) => setShippingMethod(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-900">Collect in Person</p>
                          <p className="text-sm text-slate-500">Pick up at our location</p>
                        </div>
                        <p className="font-medium text-green-600">FREE</p>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        We&apos;ll email you when your order is ready
                      </p>
                    </div>
                  </label>
                </CardContent>
              </Card>
            )}

            {/* Shipping Address - Only show for delivery */}
            {requiresShipping && !isCollection && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Delivery Address</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="address1">Address Line 1 *</Label>
                    <Input
                      id="address1"
                      value={shippingAddress.line1}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, line1: e.target.value }))}
                      placeholder="123 Main Street"
                      data-testid="checkout-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address2">Address Line 2</Label>
                    <Input
                      id="address2"
                      value={shippingAddress.line2}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, line2: e.target.value }))}
                      placeholder="Apartment, suite, etc."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="London"
                        data-testid="checkout-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="county">County</Label>
                      <Input
                        id="county"
                        value={shippingAddress.county}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, county: e.target.value }))}
                        placeholder="Greater London"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postcode">Postcode *</Label>
                      <Input
                        id="postcode"
                        value={shippingAddress.postcode}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, postcode: e.target.value }))}
                        placeholder="SW1A 1AA"
                        data-testid="checkout-postcode"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="GB"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SquarePaymentForm
                  amount={Math.round(total * 100)}
                  currency="GBP"
                  customPaymentEndpoint={`${API_URL}/api/shop/checkout`}
                  customPaymentBody={getCheckoutPayload()}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  disabled={processing || !isFormValid()}
                  buttonText={`Pay £${total.toFixed(2)}`}
                  title="Secure payment powered by Square"
                  customerName={customerInfo.name}
                  customerEmail={customerInfo.email}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Order Summary */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-3">
                  {cart?.items?.map((item, idx) => (
                    <div key={idx} className="flex gap-3">
                      {item.image ? (
                        <img
                          src={item.image.startsWith('/') ? `${API_URL}${item.image}` : item.image}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-slate-100 rounded flex items-center justify-center">
                          <Package className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium line-clamp-1">{item.name}</p>
                        <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
                        {item.is_backorder && (
                          <p className="text-xs text-amber-600 font-medium">Backorder - slight delay</p>
                        )}
                        <p className="text-sm font-medium">{formatCurrency(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Discount Code */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Discount code"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    className="flex-1"
                  />
                  <Button variant="outline" size="sm" disabled>
                    Apply
                  </Button>
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>

                  {/* Discount Code */}
                  <div className="py-2">
                    {discountApplied ? (
                      <div className="flex items-center justify-between bg-green-50 p-2 rounded-md border border-green-200">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">
                            {discountApplied.code}: {discountApplied.display}
                          </span>
                        </div>
                        <button 
                          onClick={removeDiscount}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Discount code"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          className="text-sm font-mono uppercase"
                          onKeyDown={(e) => e.key === 'Enter' && applyDiscountCode()}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={applyDiscountCode}
                          disabled={applyingDiscount || !discountCode.trim()}
                        >
                          {applyingDiscount ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Apply'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {requiresShipping && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Shipping</span>
                      {isCollection ? (
                        <span className="text-green-600">Free (Collection)</span>
                      ) : (
                        <span>{formatCurrency(shippingCost)}</span>
                      )}
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(discount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>

                <p className="text-xs text-slate-500 text-center mt-4">
                  By placing this order, you agree to our terms and conditions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
